export type DepartureWeatherSample = {
  time: Date;
  precipitationMm?: number | null;
  precipProbability?: number | null;
  /** Optional intensity used to break equal rain-exposure ties. */
  severity?: number | null;
};

export type RainWindow = { start: Date; end: Date; minutes: number };

export type DepartureCandidate = {
  departure: Date;
  samples: DepartureWeatherSample[];
};

export type DepartureWaypointForecast = {
  /** Seconds from departure to this waypoint, from the selected route. */
  travelTimeSec: number;
  /** Hourly rows already loaded for this waypoint. */
  hourly: DepartureWeatherSample[];
};

export type BestDepartureResult = {
  departure: Date;
  rainMinutes: number;
  severity: number;
  rainy: boolean;
};

export function isSameLocalDate(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function roundUpToNextQuarter(date: Date): Date {
  const rounded = new Date(date);
  rounded.setSeconds(0, 0);
  const remainder = rounded.getMinutes() % 15;
  if (remainder !== 0 || date.getSeconds() !== 0 || date.getMilliseconds() !== 0) {
    rounded.setMinutes(rounded.getMinutes() + (15 - remainder));
  }
  return rounded;
}

/** The local departure range used by the slider, without fetching new forecasts. */
export function departureTimesForSlider(
  chosenDeparture: Date,
  now = new Date(),
  stepMinutes = 30,
): Date[] {
  const chosenMs = chosenDeparture.getTime();
  const nowMs = now.getTime();
  const isToday = isSameLocalDate(chosenDeparture, now);

  // Center window around chosenDeparture (-2h to +6h), clamped to now if today
  const minStartMs = isToday ? roundUpToNextQuarter(now).getTime() : chosenMs - 2 * 60 * 60_000;
  const startMs = isToday ? Math.max(minStartMs, chosenMs - 2 * 60 * 60_000) : minStartMs;
  const endMs = Math.max(startMs + 4 * 60 * 60_000, chosenMs + 6 * 60 * 60_000);

  const departures: Date[] = [];
  if (isToday && nowMs < startMs) {
    departures.push(new Date(now));
  }
  for (let time = startMs; time <= endMs; time += stepMinutes * 60_000) {
    departures.push(new Date(time));
  }
  return departures;
}

export const DEFAULT_RAIN_THRESHOLD_MM = 0.1;

function nearestSample(samples: DepartureWeatherSample[], target: Date): DepartureWeatherSample | undefined {
  let best: DepartureWeatherSample | undefined;
  let bestDelta = Infinity;
  for (const sample of samples) {
    const delta = Math.abs(sample.time.getTime() - target.getTime());
    if (delta < bestDelta) {
      best = sample;
      bestDelta = delta;
    }
  }
  return best;
}

/**
 * Builds departure candidates from the hourly rows already in memory. This keeps the
 * departure slider deterministic and avoids a network request for every slider tick.
 */
export function buildDepartureCandidates(
  departures: Date[],
  waypointForecasts: DepartureWaypointForecast[],
): DepartureCandidate[] {
  return departures
    .filter((departure) => Number.isFinite(departure.getTime()))
    .map((departure) => ({
      departure,
      samples: waypointForecasts
        .map((waypoint) => nearestSample(waypoint.hourly, new Date(departure.getTime() + waypoint.travelTimeSec * 1000)))
        .filter((sample): sample is DepartureWeatherSample => sample != null),
    }));
}

export function isRainySample(sample: DepartureWeatherSample, rainThresholdMm = DEFAULT_RAIN_THRESHOLD_MM): boolean {
  return (sample.precipitationMm ?? 0) >= rainThresholdMm || (sample.precipProbability ?? 0) >= 50;
}

function sorted(samples: DepartureWeatherSample[]): DepartureWeatherSample[] {
  return samples.filter((sample) => Number.isFinite(sample.time.getTime())).sort((a, b) => a.time.getTime() - b.time.getTime());
}

export function rainWindow(samples: DepartureWeatherSample[], rainThresholdMm = DEFAULT_RAIN_THRESHOLD_MM): RainWindow | null {
  const ordered = sorted(samples);
  let start: Date | null = null;
  let last: Date | null = null;
  for (const sample of ordered) {
    const rainy = isRainySample(sample, rainThresholdMm);
    if (rainy && !start) {
      start = sample.time;
      last = sample.time;
    } else if (rainy && start && last) {
      last = sample.time;
    } else if (!rainy && start && last) {
      return { start, end: last, minutes: Math.max(0, Math.round((last.getTime() - start.getTime()) / 60_000)) };
    }
  }
  return start && last
    ? { start, end: last, minutes: Math.max(0, Math.round((last.getTime() - start.getTime()) / 60_000)) }
    : null;
}

export function routeRainSummary(candidates: DepartureCandidate[], departure: Date, rainThresholdMm = DEFAULT_RAIN_THRESHOLD_MM): RainWindow | null {
  const selected = candidates.reduce<DepartureCandidate | undefined>((closest, candidate) =>
    !closest || Math.abs(candidate.departure.getTime() - departure.getTime()) < Math.abs(closest.departure.getTime() - departure.getTime()) ? candidate : closest, undefined);
  return rainWindow(selected?.samples ?? [], rainThresholdMm);
}

export function formatRainWindow(window: RainWindow | null, locale = 'en-US'): string {
  if (!window) return 'Dry all the way';
  const formatter = new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' });
  const start = formatter.format(window.start);
  const end = formatter.format(window.end);
  const startMeridiem = start.match(/\s([AP]M)$/i)?.[1];
  const endMeridiem = end.match(/\s([AP]M)$/i)?.[1];
  const sameMeridiem = startMeridiem && endMeridiem && startMeridiem.toLowerCase() === endMeridiem.toLowerCase();
  const compactStart = sameMeridiem ? start.replace(/\s[AP]M$/i, '') : start;
  return `${compactStart}–${end} (${window.minutes} min)`;
}

export function pickBestDeparture(
  candidates: DepartureCandidate[],
  chosenDeparture: Date,
  rainThresholdMm = DEFAULT_RAIN_THRESHOLD_MM,
): BestDepartureResult | null {
  if (!candidates.length) return null;
  const scored = candidates.map((candidate) => {
    const window = rainWindow(candidate.samples, rainThresholdMm);
    const rainySamples = candidate.samples.filter((sample) => isRainySample(sample, rainThresholdMm));
    const severity = rainySamples.reduce((total, sample) => total + Math.max(0, sample.severity ?? sample.precipitationMm ?? 0), 0);
    return { candidate, rainMinutes: window?.minutes ?? 0, severity };
  });
  scored.sort((a, b) => a.rainMinutes - b.rainMinutes || a.severity - b.severity || Math.abs(a.candidate.departure.getTime() - chosenDeparture.getTime()) - Math.abs(b.candidate.departure.getTime() - chosenDeparture.getTime()));
  const best = scored[0]!;
  const chosen = scored.reduce((closest, item) =>
    Math.abs(item.candidate.departure.getTime() - chosenDeparture.getTime()) <
    Math.abs(closest.candidate.departure.getTime() - chosenDeparture.getTime())
      ? item
      : closest,
  );
  // A Best marker is guidance only when it actually reduces rain exposure for the chosen time.
  if (best.rainMinutes >= chosen.rainMinutes) return null;
  return { departure: best.candidate.departure, rainMinutes: best.rainMinutes, severity: best.severity, rainy: best.rainMinutes > 0 || best.candidate.samples.some((sample) => isRainySample(sample, rainThresholdMm)) };
}
